import React, { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useCreateContentSubmission } from "../../hooks/useContentSubmissions";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { contentKeys } from "../../services/content.keys";
import type { ContentPlatform, ContentType } from "../../types/content.types";
import { toast } from "@/components/ui/use-toast";
import { Sparkles, Link2, Star, ArrowLeft } from "lucide-react";

import {
  PLATFORM_OPTIONS,
  CONTENT_TYPE_OPTIONS,
} from "../posts/constants";
import { PostMetricsFields } from "../posts/PostMetricsFields";
import {
  addPostFormSchema,
  type AddPostFormValues,
} from "../../validators/content.validators";

export interface AddPostModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignInfluencerId?: string | null;
  influencerName?: string;
  availableInfluencers?: { campaign_influencer_id: string; influencer_name: string }[];
  onBack?: () => void;
}

export type { AddPostFormValues };

interface ScrapeStartResponse {
  status: "started" | "success" | "failed";
  message?: string;
  error?: string;
  details?: string;
  remaining_minutes?: number;
}

export const AddPostModal: React.FC<AddPostModalProps> = ({
  open,
  onOpenChange,
  campaignInfluencerId,
  influencerName,
  availableInfluencers,
  onBack,
}) => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id ?? "";
  const createSubmission = useCreateContentSubmission(orgId);
  const queryClient = useQueryClient();
  const [selectedInfluencerId, setSelectedInfluencerId] = useState(campaignInfluencerId || "");
  const [isFetchingMetrics, setIsFetchingMetrics] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedInfluencerId(campaignInfluencerId || "");
    }
  }, [open, campaignInfluencerId]);

  const invalidateSubmissionQueries = useCallback(
    (submissionId: string, targetInfluencerId: string) => {
      // New domain keys
      queryClient.invalidateQueries({ queryKey: contentKeys.posts(orgId, targetInfluencerId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.metricsHistory(orgId, submissionId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.topPosts(orgId) });
      queryClient.invalidateQueries({ queryKey: ["deliverable-aggregates"] });

      // Legacy fallback keys
      queryClient.invalidateQueries({ queryKey: ["influencer-posts", targetInfluencerId] });
      queryClient.invalidateQueries({ queryKey: ["metrics-history", submissionId] });
      queryClient.invalidateQueries({ queryKey: ["top-performing-posts"] });
    },
    [queryClient, orgId]
  );

  const fetchMetricsForSubmission = useCallback(
    async (submissionId: string, targetInfluencerId: string) => {
      setIsFetchingMetrics(true);

      const { data, error } = await supabase.functions.invoke<ScrapeStartResponse>("scrape-post-metrics", {
        body: { submission_id: submissionId },
      });

      if (error) {
        let errorDetail = error.message;
        try {
          if ("context" in error && error.context) {
            const res = error.context as Response;
            const errorBody = await res.json();
            errorDetail = errorBody.details || errorBody.error || errorBody.message || error.message;
          }
        } catch {
          // ignore json parse error
        }
        throw new Error(errorDetail || "We couldn't start fetching the post metrics. Please try again.");
      }

      if (data?.error) {
        if (data.error === "cooldown") {
          throw new Error(`Please wait ${data.remaining_minutes || 15} minutes before fetching metrics again.`);
        }
        throw new Error(data.details || data.error);
      }

      if (data?.status === "success") {
        setIsFetchingMetrics(false);
        invalidateSubmissionQueries(submissionId, targetInfluencerId);
        toast({
          title: "Success",
          description: "Post added and metrics fetched successfully",
        });
        return;
      }

      if (data?.status === "failed") {
        throw new Error(data.message || "We couldn't fetch the post metrics. Please try again.");
      }

      setIsFetchingMetrics(false);
      invalidateSubmissionQueries(submissionId, targetInfluencerId);
    },
    [invalidateSubmissionQueries]
  );

  const form = useForm<AddPostFormValues>({
    resolver: zodResolver(addPostFormSchema),
    defaultValues: {
      platform: "instagram",
      type: "reel",
      post_url: "",
      views: undefined,
      likes: undefined,
      comments: undefined,
      shares: undefined,
      saves: undefined,
    },
  });

  const onSubmit = (values: AddPostFormValues) => {
    const targetInfluencerId = campaignInfluencerId || selectedInfluencerId;
    if (!targetInfluencerId) {
      toast({
        title: "Error",
        description: "Please select an influencer first",
        variant: "destructive",
      });
      return;
    }

    const hasMetrics =
      values.views !== undefined ||
      values.likes !== undefined ||
      values.comments !== undefined ||
      values.shares !== undefined ||
      values.saves !== undefined;
    const shouldAutoFetchMetrics = !hasMetrics && values.post_url.includes("instagram.com");

    createSubmission.mutate(
      {
        campaign_influencer_id: targetInfluencerId,
        platform: values.platform as ContentPlatform,
        type: values.type as ContentType,
        post_url: values.post_url || undefined,
        performance_metrics: hasMetrics
          ? {
              views: values.views,
              likes: values.likes,
              comments: values.comments,
              shares: values.shares,
              saves: values.saves,
              extraction_source: "manual",
            }
          : undefined,
      },
      {
        onSuccess: (submission) => {
          form.reset();
          onOpenChange(false);

          if (shouldAutoFetchMetrics) {
            fetchMetricsForSubmission(submission.id, targetInfluencerId).catch((error: unknown) => {
              setIsFetchingMetrics(false);
              toast({
                title: "Post added successfully",
                description:
                  "The post was added, but we couldn't fetch its latest metrics automatically. You can try fetching the metrics again from the post.",
              });
            });
          }
        },
      }
    );
  };

  const currentPlatform = form.watch("platform");
  const selectedPlatformOpt =
    PLATFORM_OPTIONS.find((opt) => opt.value === currentPlatform) || PLATFORM_OPTIONS[0];

  const currentType = form.watch("type");
  const selectedTypeOpt =
    CONTENT_TYPE_OPTIONS.find((opt) => opt.value === currentType) || CONTENT_TYPE_OPTIONS[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[620px] rounded-[24px] p-6 [&>button]:bg-[#FAF5FF] [&>button]:text-[#A655F8] [&>button]:hover:bg-[#F3E8FF] [&>button]:rounded-full [&>button]:p-1.5 [&>button]:right-6 [&>button]:top-6 [&>button]:transition-colors [&>button>svg]:h-4 [&>button>svg]:w-4 [&>button]:opacity-100 [&>button]:border-none [&>button]:ring-0 [&>button]:focus:ring-0 shadow-xl border border-slate-100 font-['Hanken_Grotesk',sans-serif] focus:outline-none focus-visible:outline-none focus:ring-0 focus:ring-offset-0">
        <DialogHeader className="flex flex-row items-center gap-3.5 space-y-0 pb-2">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="w-9 h-9 rounded-full bg-[#FAF5FF] hover:bg-[#F3E8FF] text-[#A655F8] flex items-center justify-center shrink-0 transition-colors"
              title="Back to Upload Links"
            >
              <ArrowLeft className="w-4 h-4 stroke-[2.5]" />
            </button>
          )}
          <div className="w-14 h-14 rounded-full bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0 border border-[#A655F8]/5 relative overflow-hidden">
            <Sparkles className="w-6 h-6 stroke-[2]" />
          </div>
          <div className="flex-1 text-left">
            <DialogTitle className="text-2xl font-bold text-slate-800 font-['Hanken_Grotesk',sans-serif]">
              {influencerName ? `Add Post for ${influencerName}` : "Add Post"}
            </DialogTitle>
            <p className="text-[16px] text-[#64748B] mt-1 font-normal leading-tight font-['Hanken_Grotesk',sans-serif]">
              Add post details and performance metrics
            </p>
          </div>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {!campaignInfluencerId && availableInfluencers && (
              <div className="space-y-2">
                <FormLabel className="text-slate-700 font-semibold text-sm">Select Influencer</FormLabel>
                <Select value={selectedInfluencerId} onValueChange={setSelectedInfluencerId}>
                  <FormControl>
                    <SelectTrigger className="w-full h-12 rounded-xl border border-slate-200 bg-white px-3 focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#A655F8] data-[state=open]:border-[#A655F8] transition-colors">
                      <SelectValue placeholder="Choose an influencer..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="rounded-xl border-slate-100 shadow-lg">
                    {availableInfluencers.map((inf) => (
                      <SelectItem
                        key={inf.campaign_influencer_id}
                        value={inf.campaign_influencer_id}
                        className="focus:bg-[#FAF5FF] focus:text-[#A655F8] rounded-lg cursor-pointer"
                      >
                        <span className="text-slate-700 font-medium text-sm">{inf.influencer_name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="platform"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#1E293B] font-medium text-sm">Platform</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full h-12 rounded-xl border-2 border-[#E2E8F0] bg-white px-3 focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#A655F8] data-[state=open]:border-[#A655F8] transition-colors [&>span]:w-full">
                          <div className="flex items-center gap-2">
                            {selectedPlatformOpt.icon}
                            <span className="text-slate-800 text-sm font-medium">{selectedPlatformOpt.label}</span>
                          </div>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-xl border-slate-100 shadow-lg">
                        {PLATFORM_OPTIONS.map((opt) => (
                          <SelectItem
                            key={opt.value}
                            value={opt.value}
                            className="focus:bg-[#FAF5FF] focus:text-[#A655F8] rounded-lg cursor-pointer"
                          >
                            <div className="flex items-center gap-2.5 py-1">
                              {opt.icon}
                              <span className="text-slate-700 font-medium text-sm">{opt.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#1E293B] font-medium text-sm">Content Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full h-12 rounded-xl border-2 border-[#E2E8F0] bg-white px-3 focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#A655F8] data-[state=open]:border-[#A655F8] transition-colors [&>span]:w-full">
                          <div className="flex items-center gap-2">
                            {selectedTypeOpt.icon}
                            <span className="text-slate-800 text-sm font-medium">{selectedTypeOpt.label}</span>
                          </div>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-xl border-slate-100 shadow-lg">
                        {CONTENT_TYPE_OPTIONS.map((opt) => (
                          <SelectItem
                            key={opt.value}
                            value={opt.value}
                            className="focus:bg-[#FAF5FF] focus:text-[#A655F8] rounded-lg cursor-pointer"
                          >
                            <div className="flex items-center gap-2.5 py-1">
                              {opt.icon}
                              <span className="text-slate-700 font-medium text-sm">{opt.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="post_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#1E293B] font-medium text-sm">Post URL</FormLabel>
                  <FormControl>
                    <div className="relative flex items-center">
                      <Link2 className="absolute left-3.5 w-5 h-5 text-slate-400" />
                      <Input
                        placeholder="https://..."
                        className="pl-11 h-12 rounded-xl border-2 border-[#E2E8F0] bg-white focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#A655F8] focus:outline-none transition-colors"
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Performance Metrics Section */}
            <PostMetricsFields control={form.control} />

            <DialogFooter className="flex items-center justify-end gap-3">
              {onBack ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onBack}
                  className="rounded-xl h-11 px-5 hover:bg-slate-50 text-slate-700 border-slate-200 font-semibold text-sm transition-colors flex items-center gap-1.5"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="rounded-xl h-11 px-6 hover:bg-slate-50 text-slate-700 border-slate-200 font-semibold text-sm transition-colors"
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                disabled={createSubmission.isPending || isFetchingMetrics}
                className="bg-[#A655F8] hover:bg-[#9042E0] text-white rounded-xl h-11 px-6 font-semibold text-sm flex items-center justify-center gap-2 shadow-sm transition-colors border border-transparent"
              >
                <Star className="w-4 h-4 fill-white text-white" />
                {createSubmission.isPending
                  ? "Adding..."
                  : isFetchingMetrics
                    ? "Fetching metrics..."
                    : "Add Post"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
